import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Screen from '../../components/common/Screen';
import Title from '../../components/common/Title';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import InputField from '../../components/forms/InputField';
import ReCaptchaWidget from '../../components/forms/ReCaptchaWidget';
import Loading from '../../components/common/Loading';
import NotFound from '../../components/common/NotFound';
import Icon from '../../components/common/Icon';
import EventCta from '../../components/common/EventCta';
import { handleBranding, formatDate } from '../../utils';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BUILD_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

/* Public referral claim page (/refer/:token). A friend was referred; opening the link lands
   them here over the event's shop banner to leave their details (name + email required, company
   + phone optional). Not a login — the person is a lead, like the anonymous cheer flow. On
   success we show "You're on the list" with Book now / Learn more CTAs (and email the same).

   Also /friend/:code (mode="code"): the same claim through a player's permanent personal code.
   While the next edition is not on sale (early_list) the page says "claim your spot on the early
   list — first to know, lowest price" and shows no date; once one is, it offers that edition. */
const ReferClaim = ({ mode = 'token' }) => {
  const { token, code } = useParams();
  const { t } = useTranslation('common');
  const apiBase = mode === 'code'
    ? `/api/friend/${encodeURIComponent(code || '')}`
    : `/api/refer/claim/${encodeURIComponent(token || '')}`;

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    handleBranding();
    (async () => {
      try {
        const res = await fetch(apiBase, {
          method: 'GET', credentials: 'include',
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.valid) { setError(body.message || 'invalidLink'); }
        else { setInfo(body); }
      } catch {
        setError('somethingWentWrong');
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase]);

  const siteKey = info?.recaptcha_site_key || BUILD_SITE_KEY;
  const eventName = info?.event_name || '';
  const early = !!info?.early_list;
  const brand = info?.brand_name || eventName;
  const referrer = info?.referrer_name || t('aFriend', { defaultValue: 'A friend' });
  const meta = info
    ? [info.event_date ? formatDate(info.event_date) : null, info.location_name]
      .filter(Boolean).join(' · ')
    : '';

  // Bravos Lab visuals are square — show a clean centred square poster (matching the storefront
  // and public event page) instead of cropping the image into a wide rectangular banner.
  const banner = (
    <>
      {info?.event_image && (
        <img src={info.event_image} alt={eventName}
             style={{ width: 'min(100%, 16rem)', aspectRatio: '1 / 1', objectFit: 'cover',
                      borderRadius: '16px', display: 'block', margin: '1rem auto 0' }} />
      )}
    </>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) { setFormError(t('fillRequiredFields', { defaultValue: 'Please fill in the required fields.' })); return; }
    if (!EMAIL_PATTERN.test(email.trim())) { setFormError(t('invalidEmail')); return; }
    if (siteKey && !captchaToken) { setFormError(t('captchaRequired', { defaultValue: 'Please confirm you are not a robot.' })); return; }
    setSubmitting(true); setFormError(null);
    try {
      const res = await fetch(`${apiBase}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          company: company.trim(),
          phone: phone.trim(),
          recaptcha_token: captchaToken,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { setFormError(t(body.message || 'somethingWentWrong', { defaultValue: 'Something went wrong.' })); }
      else { setInfo((prev) => ({ ...prev, ...body })); setDone(true); }
    } catch {
      setFormError(t('somethingWentWrong', { defaultValue: 'Something went wrong.' }));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loading />;
  if (error) return <NotFound />;

  return (
    <Screen color="#f3f4f6" p="3">
      <div className="top" style={{ width: '100%', maxWidth: '560px', margin: '0 auto' }}>
        {done ? (
          <Card p="0" className="text-center">
            {banner}
            <div className="p-3">
              <div style={{ fontSize: '2rem' }}>🎉</div>
              <Title heading="3" my="1" className="mb-2">
                {early ? t('referCodeThanksTitle') : t('referClaimThanksTitle')}
              </Title>
              <p className="text-secondary mb-3">
                {early ? t('referCodeThanksBody', { brand }) : t('referClaimThanksBody')}
              </p>

              {info?.book_url && (
                <>
                  <p className="fw-semibold mb-1">{t('referAlreadyConvinced')}</p>
                  <a href={info.book_url} className="public-cta-link d-inline-block mb-3">
                    <Icon name="ticket" /> {t('bookNow')}
                  </a>
                </>
              )}
              {(info?.commercial_url || info?.book_url) && (
                <>
                  <p className="fw-semibold mb-1">{t('referNeedMore')}</p>
                  <a href={info.commercial_url || info.book_url}
                     target={info.commercial_url ? '_blank' : undefined}
                     rel={info.commercial_url ? 'noreferrer' : undefined}
                     className="public-cta-link d-inline-block">
                    {t('learnMore', { defaultValue: 'Learn more' })}
                  </a>
                </>
              )}
            </div>
          </Card>
        ) : (
          <Card p="0">
            {banner}
            <div className="p-3">
              <div className="public-kicker mb-2">
                {early ? t('referCodeKicker', { brand }) : `${eventName}${meta ? ` · ${meta}` : ''}`}
              </div>
              <Title heading="3" my="0" className="mb-2">
                {early ? t('referCodeClaimTitle', { name: referrer, brand }) : t('referClaimTitle', { name: referrer })}
              </Title>

              {info?.book_mode && info?.book_url ? (
                <>
                  {/* Book-mode (per-Lab invite): the direct payment link is the primary action. */}
                  <p className="text-secondary mb-3">{t('referClaimBookIntro')}</p>
                  <a href={info.book_url} className="public-cta-link d-inline-block mb-3">
                    <Icon name="ticket" /> {t('bookAndPay')}
                  </a>
                  <p className="fw-semibold mb-2">{t('referOrLeaveDetails')}</p>
                </>
              ) : (
                <p className="text-secondary mb-3">{early ? t('referCodeClaimIntro') : t('referClaimIntro')}</p>
              )}

              <form onSubmit={handleSubmit}>
                <div className="row g-2">
                  <div className="col-md-6">
                    <InputField type="text" name="firstName" value={firstName}
                      label={<>{t('firstName')} <span className="text-danger">*</span></>}
                      onChange={(e) => { setFirstName(e.target.value); if (formError) setFormError(null); }}
                      required maxLength={40} />
                  </div>
                  <div className="col-md-6">
                    <InputField type="text" name="lastName" value={lastName}
                      label={<>{t('lastName')} <span className="text-danger">*</span></>}
                      onChange={(e) => { setLastName(e.target.value); if (formError) setFormError(null); }}
                      required maxLength={50} />
                  </div>
                </div>

                <InputField type="email" name="email" placeholder="you@example.com" value={email}
                  label={<>{t('email')} <span className="text-danger">*</span></>}
                  onChange={(e) => { setEmail(e.target.value); if (formError) setFormError(null); }}
                  required maxLength={120} />

                <InputField type="text" name="company" label={t('companyOptional')} value={company}
                  onChange={(e) => setCompany(e.target.value)} maxLength={120} />

                <InputField type="tel" name="phone" label={t('phoneOptional')} value={phone}
                  onChange={(e) => setPhone(e.target.value)} maxLength={40} />

                {siteKey && (
                  <div className="mb-3">
                    <ReCaptchaWidget
                      siteKey={siteKey}
                      onVerify={(tok) => { setCaptchaToken(tok); if (formError) setFormError(null); }}
                      onExpire={() => setCaptchaToken('')}
                      onError={() => setCaptchaToken('')}
                    />
                  </div>
                )}

                {formError && <p className="text-danger text-start mb-3">{formError}</p>}

                <Button type="submit" disabled={submitting || (siteKey && !captchaToken)}>
                  {submitting ? t('pleaseWait') : (early ? t('referCodeClaimSubmit') : t('referClaimSubmit'))}
                </Button>
              </form>
            </div>
          </Card>
        )}

        {/* Event explanation badge — shown to the RECEIVING party (moved off the sender page). */}
        {eventName ? (
          <EventCta eventName={early ? brand : eventName} commercialUrl={info?.commercial_url} />
        ) : null}
      </div>
    </Screen>
  );
};

export default ReferClaim;
